import { useEffect, useRef, type RefObject } from "react";

/**
 * Trap keyboard focus inside a dialog while ``open`` is true (WCAG 2.1.2,
 * inverse of "no keyboard trap" - the user must be able to Tab forward
 * and back without escaping the modal).
 *
 * On open: moves focus to the first focusable child after a microtask.
 * Tab / Shift+Tab cycle through focusables (computed live each keypress
 * so dynamic content stays in the cycle). Esc calls onClose by default.
 * On close: restores focus to the previously-focused element.
 *
 * Returns a ref that the modal must attach to its outermost element.
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onClose?: () => void,
  options: { escClosesDialog?: boolean } = {},
): RefObject<T | null> {
  const containerRef = useRef<T | null>(null);
  const escClosesDialog = options.escClosesDialog ?? true;

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent): void => {
      if (escClosesDialog && e.key === "Escape" && onClose) {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !containerRef.current) return;
      const focusables = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("aria-hidden") && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const focusTimer = window.setTimeout(() => {
      const focusable = containerRef.current?.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }, 0);

    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKey);
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [open, onClose, escClosesDialog]);

  return containerRef;
}
